/**
 * A form the studio sent (`/form-request/:requestToken`, web-account-pages D6):
 * opened by its link alone, signed in or not, since the token is the
 * authority. The `form-request` piece in the website's look; below 768px the
 * app's own form screen (FormRequestScreen, shared-client-screens D3), whose
 * Back always shows (F1) and returns where the client came from on this site,
 * the messages included (F2), or the studio's home when the form was the first
 * page they opened.
 */
import { useEffect } from 'react';
import { useParams } from 'react-router-dom';

import { FormRequestScreen } from '@/pages/FormRequestPage/FormRequestScreen';

import { useSiteBack } from '../pageMover';
import { FormRequestView } from '../pieces/form-request/FormRequestView';
import { useFormRequest } from '../pieces/form-request';
import { useAppScreens } from '../ui/useIsPhone';
import { usePageTitle } from '../ui/usePageTitle';

export function FormRequestWebPage() {
  const { requestToken = '' } = useParams<{ requestToken: string }>();
  // Each link is its own page: a pending save of the form left goes through that form's own token (Codex review 2026-10-26).
  return <FormRequestWebPageFor key={requestToken} requestToken={requestToken} />;
}

/** The form's address sends no referrer while it is open, however the client got here (web-account-pages D6). */
function useNoReferrer() {
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'referrer';
    meta.content = 'no-referrer';
    document.head.appendChild(meta);
    return () => {
      // The rest of the site's policy, as its pages are served.
      meta.content = 'strict-origin-when-cross-origin';
      meta.remove();
    };
  }, []);
}

function FormRequestWebPageFor({ requestToken }: { readonly requestToken: string }) {
  useNoReferrer();
  const runtime = useFormRequest({ requestToken });
  const phone = useAppScreens();
  const back = useSiteBack();
  usePageTitle('Form');
  if (!phone) return <FormRequestView {...runtime} />;
  const { state, actions } = runtime;
  return (
    <FormRequestScreen
      status={state.status}
      form={runtime.form}
      isAnonymous={state.anonymous}
      serverErrors={runtime.serverErrors}
      submitFailed={state.submitFailed}
      onBack={back}
      onSubmit={actions.submit}
      onAnswersChange={runtime.answersChanged}
      onHome={actions.goHome}
      onRetry={actions.retry}
    />
  );
}
